import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, CheckCircle2, Eye, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/lib/live";
import { fmtDate, fmtDateTime, rupiah, todayStr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AuthImage } from "@/components/AuthImage";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatCard } from "@/components/common";
import { InvoiceStatus } from "@/components/InvoiceStatus";

const PERIOD = (p) => new Date(`${p}-01T00:00:00`).toLocaleDateString("id-ID", { month: "long", year: "numeric" });

const ProofView = ({ inv, onClose }) => (
  <Dialog open={Boolean(inv)} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md" data-testid="proof-dialog">
      <DialogHeader><DialogTitle className="font-heading">Bukti Bayar {inv && PERIOD(inv.period)}</DialogTitle></DialogHeader>
      {inv && <>
        <AuthImage path={`/billing/invoices/${inv.id}/proof?v=${encodeURIComponent(inv.proof_at || "")}`} alt="Bukti bayar" className="w-full max-h-[60vh] object-contain rounded-2xl bg-muted" testId="proof-image" />
        <p className="text-xs text-muted-foreground">Dikirim {fmtDateTime(inv.proof_at)} oleh {inv.proof_by_name}{inv.proof_note ? ` · ${inv.proof_note}` : ""}</p>
      </>}
    </DialogContent>
  </Dialog>
);

const UploadProof = ({ inv, onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setFile(null); setNote(""); }, [inv]);
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("note", note);
      await api.post(`/billing/invoices/${inv.id}/proof`, fd);
      toast.success("Pengajuan pembayaran dikirim"); onClose(); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={Boolean(inv)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="upload-proof-dialog">
        <DialogHeader><DialogTitle className="font-heading">Kirim Bukti Pembayaran</DialogTitle></DialogHeader>
        {inv && <p className="text-sm">Tagihan {PERIOD(inv.period)} · <b className="font-mono">{rupiah(inv.amount)}</b></p>}
        <label className="block rounded-2xl border-2 border-dashed p-5 text-center cursor-pointer hover:bg-muted/50" data-testid="proof-file-label">
          <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
          <span className="text-sm">{file ? file.name : "Pilih foto bukti transfer"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="proof-file-input" />
        </label>
        <Input className="h-11" placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="proof-note-input" />
        <DialogFooter><Button className="rounded-full h-11 px-6" disabled={!file || busy} onClick={send} data-testid="proof-submit">{busy ? "Mengirim..." : "Kirim Pengajuan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Actions = ({ inv, admin, overdue, onView, onPay, onReject, onUpload }) => (
  <div className="flex flex-wrap justify-end gap-1.5">
    {inv.has_proof && <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => onView(inv)} data-testid={`invoice-proof-${inv.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Bukti Bayar</Button>}
    {admin && inv.status !== "lunas" && <Button size="sm" className="h-8 rounded-full" onClick={() => onPay(inv)} data-testid={`invoice-paid-${inv.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Dibayar</Button>}
    {admin && inv.status === "menunggu" && <Button size="sm" variant="ghost" className="h-8 rounded-full text-rose-600" onClick={() => onReject(inv)} data-testid={`invoice-reject-${inv.id}`}><XCircle className="h-3.5 w-3.5 mr-1" />Tolak</Button>}
    {!admin && inv.status !== "lunas" && <Button size="sm" variant={overdue ? "default" : "outline"} className="h-8 rounded-full" onClick={() => onUpload(inv)} data-testid={`invoice-upload-${inv.id}`}><Upload className="h-3.5 w-3.5 mr-1" />{inv.status === "menunggu" ? "Kirim Ulang" : "Bayar"}</Button>}
  </div>
);

const InvoiceTable = ({ rows, ...p }) => (
  <div className="panel !p-0 overflow-x-auto" data-testid="invoice-table">
    <table className="w-full text-sm min-w-[820px]">
      <thead><tr className="text-left text-xs text-muted-foreground border-b">
        <th className="py-3 pl-5 font-medium">Periode</th><th className="py-3 px-3 font-medium">Tanggal Tagihan</th><th className="py-3 px-3 font-medium">Jatuh Tempo</th>
        <th className="py-3 px-3 font-medium text-right">Resort × Biaya</th><th className="py-3 px-3 font-medium text-right">Total</th><th className="py-3 px-3 font-medium">Status</th><th className="py-3 pr-5" />
      </tr></thead>
      <tbody>
        {rows.map((i) => (
          <tr key={i.id} className="border-b last:border-0" data-testid={`invoice-row-${i.id}`}>
            <td className="py-3 pl-5 font-medium">{PERIOD(i.period)}</td>
            <td className="py-3 px-3 whitespace-nowrap">{fmtDate(i.issue_date)}</td>
            <td className="py-3 px-3 whitespace-nowrap">{fmtDate(i.due_date)}</td>
            <td className="py-3 px-3 text-right font-mono whitespace-nowrap">{i.resort_count} × {rupiah(i.fee_per_resort)}</td>
            <td className="py-3 px-3 text-right font-mono font-semibold whitespace-nowrap" data-testid={`invoice-amount-${i.id}`}>{rupiah(i.amount)}</td>
            <td className="py-3 px-3"><InvoiceStatus s={i.status} overdue={i.due_date < todayStr()} testId={`invoice-status-${i.id}`} />{i.reject_note && i.status === "belum_bayar" && <p className="text-[11px] text-rose-600 mt-1">Ditolak: {i.reject_note}</p>}</td>
            <td className="py-3 pr-5"><Actions inv={i} overdue={i.due_date < todayStr()} {...p} /></td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground" data-testid="invoice-empty">Belum ada tagihan.</td></tr>}
      </tbody>
    </table>
  </div>
);

const OfficeToggle = ({ w, onToggle }) => (
  <label className="flex items-center gap-3 rounded-full border px-4 h-11" data-testid="wilayah-active-toggle-wrap">
    <span className="text-sm font-medium">Kantor Wilayah {w.active ? "Aktif" : "Nonaktif"}</span>
    <Switch checked={w.active} onCheckedChange={onToggle} data-testid="wilayah-active-toggle" />
  </label>
);

export const BillingDetail = ({ admin }) => {
  const { wid } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [view, setView] = useState(null);
  const [up, setUp] = useState(null);
  const [pay, setPay] = useState(null);
  const [rej, setRej] = useState(null);
  const load = useCallback(() => { api.get(admin ? `/billing/wilayah/${wid}` : "/billing/my").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, [admin, wid]);
  useEffect(() => { load(); }, [load]);
  usePoll(load, 15000);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (err && !d) return <p className="py-10 text-center text-muted-foreground" data-testid="billing-error">{err}</p>;
  if (!d) return <p className="text-muted-foreground">Memuat...</p>;
  const b = d.wilayah.billing;
  const unpaid = d.invoices.filter((i) => i.status !== "lunas").reduce((s, i) => s + i.amount, 0);
  const off = d.cabang.filter((c) => c.billing_suspended).length;
  return (
    <div>
      {admin && <button type="button" onClick={() => navigate("/tagihan-layanan")} className="inline-flex items-center text-sm text-muted-foreground mb-3" data-testid="billing-back"><ArrowLeft className="h-4 w-4 mr-1" />Kembali</button>}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Tagihan Layanan</p><h1 className="font-heading text-2xl sm:text-3xl font-bold" data-testid="billing-title">{admin ? d.wilayah.name : "Tagihan Bulanan Resort"}</h1></div>
        {admin && <OfficeToggle w={d.wilayah} onToggle={(v) => act(() => api.patch(`/wilayah/${d.wilayah.id}/status`, { active: v }), v ? "Kantor wilayah diaktifkan" : "Kantor wilayah dinonaktifkan")} />}
      </div>
      {d.overdue && <div className="mb-5 rounded-2xl border border-rose-300 bg-rose-500/10 px-4 py-3 text-sm" data-testid="billing-overdue-alert">Ada tagihan melewati jatuh tempo. {off} kantor cabang dinonaktifkan otomatis sampai tagihan dilunasi.</div>}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Biaya per Resort" value={b ? rupiah(b.fee_per_resort) : "-"} hint={b ? `Tagihan tgl ${b.bill_day} · tempo ${b.due_days} hari` : "Belum diatur"} testId="billing-card-fee" />
        <StatCard label="Jumlah Resort" value={String(d.resort_count)} hint={b ? `Estimasi ${rupiah(b.fee_per_resort * d.resort_count)}/bulan` : ""} testId="billing-card-resort" />
        <StatCard label="Belum Dibayar" value={rupiah(unpaid)} icon={Building2} testId="billing-card-unpaid" />
      </div>
      <InvoiceTable rows={d.invoices} admin={admin} onView={setView} onPay={setPay} onReject={setRej} onUpload={setUp} />
      <ProofView inv={view} onClose={() => setView(null)} />
      <UploadProof inv={up} onClose={() => setUp(null)} onSaved={load} />
      <ConfirmDialog open={Boolean(pay)} onOpenChange={(o) => !o && setPay(null)} title="Tandai Dibayar" confirmLabel="Ya, lunas" onConfirm={() => act(() => api.post(`/billing/invoices/${pay.id}/paid`), "Tagihan ditandai lunas")}
        description={pay && <>Tandai tagihan {PERIOD(pay.period)} sebesar <b>{rupiah(pay.amount)}</b> sebagai lunas? Kantor cabang yang dinonaktifkan karena tagihan akan aktif kembali.</>} />
      <ConfirmDialog open={Boolean(rej)} onOpenChange={(o) => !o && setRej(null)} title="Tolak Bukti Bayar" danger confirmLabel="Ya, tolak" onConfirm={() => act(() => api.post(`/billing/invoices/${rej.id}/reject`, { note: "Bukti tidak valid" }), "Pengajuan ditolak")}
        description={rej && <>Tolak bukti bayar tagihan {PERIOD(rej.period)}? Kantor wilayah perlu mengirim ulang.</>} />
    </div>
  );
};

export default function TagihanLayananDetailPage() { return <BillingDetail admin />; }
export const TagihanWilayahPage = () => <BillingDetail admin={false} />;
